import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FiMail, FiSend, FiInbox, FiTrash2, FiStar, FiAlertTriangle, FiPlus, FiPaperclip } from 'react-icons/fi';
import { useSector } from '../contexts/SectorContext';
import { getEmailConnectionStatus, triggerEmailSync } from '../services/EmailConnectionService';
import {
  listEmails,
  getEmailById,
  markRead,
  toggleStar,
  moveEmail,
  deleteEmailPermanently,
  sendEmail,
  replyEmail,
  linkEmailToContact,
  MANUALLY_LINKABLE_STATUSES,
  LINKED_STATUSES,
  type EmailListItem,
  type EmailDetail,
  type SendEmailRequest,
  type ReplyEmailRequest,
} from '../services/EmailMessageService';
import { optimizeText } from '../services/TextOptimizationService';
import { getContactsPaged } from '../services/ContactService';
import type { ContactListDTO } from '../types/pagedAndDtos';
import { LoadingSpinner } from '../components/LoadingSpinner';
import Toast, { ToastContainer } from '../components/Toast';
import { ConfirmModal } from '../components/SharedModal';
import './EmailPage.css';

/** Por que o e-mail não virou cliente (email_messages.link_status), em português: rótulo curto e explicação. */
const LINK_REASONS: Record<string, { label: string; detail: string }> = {
  automated: { label: 'Automático', detail: 'Envio automático (aviso de sistema, newsletter, cobrança ou notificação).' },
  prospecting: { label: 'Prospecção', detail: 'Alguém oferecendo serviço ou produto para a empresa.' },
  job_candidate: { label: 'Candidato a vaga', detail: 'Currículo ou pedido de emprego.' },
  uncertain: { label: 'Incerto', detail: 'A IA não teve certeza se é cliente. Se for, ligue a um cliente.' },
  internal: { label: 'Equipe', detail: 'E-mail da própria equipe da conta.' },
  ambiguous: { label: 'Homônimo', detail: 'Mais de um cliente tem este e-mail, ou foi enviado para clientes diferentes.' },
  no_match: { label: 'Sem cliente', detail: 'Enviado para quem ainda não é cliente.' },
  skipped_folder: { label: 'Spam/lixeira', detail: 'Spam e lixeira ficam fora de Conversas.' },
};

function linkReason(status: string | null | undefined) {
  if (!status) return { label: 'Aguardando análise', detail: 'Ainda não foi analisado; acontece na próxima leitura da caixa.' };
  if (LINKED_STATUSES.includes(status)) return null;
  return LINK_REASONS[status] ?? { label: 'Sem cliente', detail: 'Não está na conversa de nenhum cliente.' };
}

/** Texto puro a partir do HTML, pelo parser nativo do navegador. */
function htmlToText(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.body.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
  doc.body.querySelectorAll('p, div').forEach((el) => {
    if (el.nextSibling) el.append('\n');
  });
  return doc.body.textContent?.trim() ?? '';
}

function isBodyEmpty(html: string): boolean {
  if (!html?.trim()) return true;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  if (doc.body.querySelector('img')) return false;
  return !(doc.body.textContent?.trim());
}

const EmailBodyEditor: React.FC<{
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  className?: string;
}> = ({ value, onChange, placeholder = 'Mensagem...', rows = 8, disabled, className }) => {
  const ref = useRef<HTMLDivElement>(null);
  const lastValueRef = useRef<string>(value);
  const isEmpty = isBodyEmpty(value);

  useEffect(() => {
    if (!ref.current) return;
    if (value !== lastValueRef.current) {
      lastValueRef.current = value;
      ref.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = () => {
    if (!ref.current) return;
    const html = ref.current.innerHTML;
    lastValueRef.current = html;
    onChange(html);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file' && items[i].type.startsWith('image/')) {
        e.preventDefault();
        const file = items[i].getAsFile();
        if (!file || !ref.current) return;
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result as string;
          const img = document.createElement('img');
          img.src = dataUrl;
          img.style.maxWidth = '100%';
          img.style.height = 'auto';
          img.style.display = 'block';
          const sel = window.getSelection();
          const range = sel?.rangeCount ? sel.getRangeAt(0) : null;
          if (range && ref.current?.contains(range.commonAncestorContainer)) {
            range.deleteContents();
            range.insertNode(img);
            range.collapse(false);
          } else if (ref.current) {
            ref.current.appendChild(img);
          }
          handleInput();
        };
        reader.readAsDataURL(file);
        return;
      }
    }
  };

  return (
    <div className={`${className || ''} email-body-editor-wrap`} style={{ position: 'relative' }}>
      {isEmpty && (
        <div className="email-body-editor-placeholder" aria-hidden>
          {placeholder}
        </div>
      )}
      <div
        ref={ref}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={handleInput}
        onPaste={handlePaste}
        className="email-body-editor"
        style={{ minHeight: rows * 22 }}
      />
    </div>
  );
};

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = (reader.result as string) || '';
      const base64 = result.split(',')[1] || result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const FOLDERS = [
  { id: 'inbox', label: 'Caixa de entrada', icon: FiInbox },
  { id: 'sent', label: 'Enviados', icon: FiSend },
  { id: 'starred', label: 'Com estrela', icon: FiStar },
  { id: 'spam', label: 'Spam', icon: FiAlertTriangle },
  { id: 'trash', label: 'Lixeira', icon: FiTrash2 },
];

const EmailPage: React.FC = () => {
  const { selectedSectorId } = useSector();
  const [folder, setFolder] = useState('inbox');
  const [emails, setEmails] = useState<EmailListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<EmailDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [hasConnection, setHasConnection] = useState(false);
  const [connectionLoading, setConnectionLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [showReply, setShowReply] = useState(false);
  const [sending, setSending] = useState(false);
  const [toasts, setToasts] = useState<{ id: number; message: string; type: 'success' | 'error' }[]>([]);
  const [confirmAction, setConfirmAction] = useState<'moveTrash' | 'moveSpam' | 'deletePermanently' | null>(null);
  const [confirmEmailId, setConfirmEmailId] = useState<number | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const syncTriggeredRef = React.useRef(false);
  const fetchEmailsRef = React.useRef<((silent?: boolean) => void) | null>(null);
  // Recargas agendadas depois de pedir uma leitura da caixa: canceladas ao sair da tela (sem setState depois do unmount).
  const pendingTimersRef = React.useRef<Set<number>>(new Set());
  const later = useCallback((fn: () => void, ms: number) => {
    const id = window.setTimeout(() => {
      pendingTimersRef.current.delete(id);
      fn();
    }, ms);
    pendingTimersRef.current.add(id);
  }, []);

  useEffect(() => {
    const timers = pendingTimersRef.current;
    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      timers.clear();
    };
  }, []);

  const addToast = (message: string, type: 'success' | 'error') => {
    setToasts((prev) => [...prev, { id: Date.now(), message, type }]);
  };

  const fetchConnectionStatus = useCallback(async () => {
    if (!selectedSectorId) return;
    setConnectionLoading(true);
    try {
      const status = await getEmailConnectionStatus(selectedSectorId);
      setHasConnection(status.hasConnection && status.status === 'connected');
    } catch {
      setHasConnection(false);
    } finally {
      setConnectionLoading(false);
    }
  }, [selectedSectorId]);

  const fetchEmails = useCallback(async (silent = false) => {
    if (!selectedSectorId || !hasConnection) return;
    if (!silent) setLoading(true);
    try {
      const res = await listEmails(selectedSectorId, folder, page, 20);
      setEmails(res.items);
      setTotal(res.total);
      // Pasta vazia e ainda sem sincronizar nesta tela: pede uma leitura e recarrega em seguida.
      const isEmpty = res.items.length === 0 && res.total === 0;
      const shouldTriggerSync = ['inbox', 'sent', 'spam', 'trash'].includes(folder) && isEmpty && !syncTriggeredRef.current;
      if (shouldTriggerSync) {
        syncTriggeredRef.current = true;
        try {
          await triggerEmailSync(selectedSectorId);
          later(() => fetchEmailsRef.current?.(false), 5000);
        } catch {
          syncTriggeredRef.current = false;
        }
      }
    } catch (err: any) {
      if (!silent) addToast(err?.response?.data?.message || 'Não foi possível carregar os e-mails.', 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [selectedSectorId, hasConnection, folder, page, later]);

  fetchEmailsRef.current = fetchEmails;

  useEffect(() => {
    fetchConnectionStatus();
  }, [fetchConnectionStatus]);

  useEffect(() => {
    syncTriggeredRef.current = false;
  }, [selectedSectorId, folder]);

  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  // A caixa é lida no servidor a cada 2 minutos (mesmo com esta tela fechada). Aqui: uma leitura ao abrir e a lista
  // recarregada em silêncio a cada 30 s; "Atualizar agora" pede outra leitura na hora.
  const syncNow = useCallback(async () => {
    if (!selectedSectorId) return;
    try {
      await triggerEmailSync(selectedSectorId);
      later(() => fetchEmailsRef.current?.(true), 4000);
    } catch {
      // Silencioso: o setor pode já estar sincronizando.
    }
  }, [selectedSectorId, later]);

  useEffect(() => {
    if (!selectedSectorId || !hasConnection) return;
    void syncNow();
    const interval = setInterval(() => fetchEmailsRef.current?.(true), 30000);
    return () => clearInterval(interval);
  }, [selectedSectorId, hasConnection, syncNow]);

  const handleSelectEmail = async (id: number) => {
    setDetailLoading(true);
    setSelectedEmail(null);
    try {
      const detail = await getEmailById(id);
      setSelectedEmail(detail);
      if (!detail.isRead) {
        await markRead(id, true);
        setEmails((prev) => prev.map((e) => (e.id === id ? { ...e, isRead: true } : e)));
      }
    } catch (err: any) {
      addToast(err?.response?.data?.message || 'Não foi possível abrir o e-mail.', 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleToggleStar = async (id: number) => {
    try {
      const res = await toggleStar(id);
      setEmails((prev) => prev.map((e) => (e.id === id ? { ...e, isStarred: res.isStarred } : e)));
      if (selectedEmail?.id === id) setSelectedEmail({ ...selectedEmail, isStarred: res.isStarred });
    } catch (err: any) {
      addToast(err?.response?.data?.message || 'Não foi possível atualizar.', 'error');
    }
  };

  const openConfirm = (action: 'moveTrash' | 'moveSpam' | 'deletePermanently', id: number) => {
    setConfirmAction(action);
    setConfirmEmailId(id);
  };

  const closeConfirm = () => {
    if (!confirmLoading) {
      setConfirmAction(null);
      setConfirmEmailId(null);
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmEmailId || !confirmAction) return;
    setConfirmLoading(true);
    try {
      if (confirmAction === 'moveTrash') {
        await moveEmail(confirmEmailId, 'trash');
        addToast('Movido para a lixeira', 'success');
      } else if (confirmAction === 'moveSpam') {
        await moveEmail(confirmEmailId, 'spam');
        addToast('Movido para o spam', 'success');
      } else if (confirmAction === 'deletePermanently') {
        await deleteEmailPermanently(confirmEmailId);
        addToast('E-mail excluído definitivamente', 'success');
      }
      setEmails((prev) => prev.filter((e) => e.id !== confirmEmailId));
      setTotal((t) => Math.max(0, t - 1));
      if (selectedEmail?.id === confirmEmailId) setSelectedEmail(null);
    } catch (err: any) {
      addToast(err?.response?.data?.message || 'Não foi possível concluir.', 'error');
    } finally {
      setConfirmLoading(false);
      setConfirmAction(null);
      setConfirmEmailId(null);
    }
  };

  const handleMoveToTrash = (id: number) => openConfirm('moveTrash', id);
  const handleMoveToSpam = (id: number) => openConfirm('moveSpam', id);
  const handleDeletePermanently = (id: number) => openConfirm('deletePermanently', id);

  const handleMoveToInbox = async (id: number) => {
    try {
      await moveEmail(id, 'inbox');
      setEmails((prev) => prev.filter((e) => e.id !== id));
      setTotal((t) => Math.max(0, t - 1));
      if (selectedEmail?.id === id) setSelectedEmail(null);
      addToast('Devolvido para a caixa de entrada', 'success');
    } catch (err: any) {
      addToast(err?.response?.data?.message || 'Não foi possível devolver.', 'error');
    }
  };

  const handleSend = async (req: SendEmailRequest) => {
    if (!selectedSectorId) return;
    setSending(true);
    try {
      await sendEmail(selectedSectorId, req);
      addToast('E-mail enviado', 'success');
      setShowCompose(false);
      setShowReply(false);
      syncTriggeredRef.current = false;
      try {
        await triggerEmailSync(selectedSectorId);
        later(() => fetchEmailsRef.current?.(), 4000);
      } catch {
        fetchEmails();
      }
    } catch (err: any) {
      addToast(err?.response?.data?.message || 'Não foi possível enviar.', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleReply = async (req: ReplyEmailRequest) => {
    if (!selectedSectorId || !selectedEmail) return;
    setSending(true);
    try {
      await replyEmail(selectedSectorId, selectedEmail.id, req);
      addToast('Resposta enviada', 'success');
      setShowReply(false);
      syncTriggeredRef.current = false;
      try {
        await triggerEmailSync(selectedSectorId);
        later(() => fetchEmailsRef.current?.(), 4000);
      } catch {
        fetchEmails();
      }
    } catch (err: any) {
      addToast(err?.response?.data?.message || 'Não foi possível enviar a resposta.', 'error');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (d: string | null) => {
    if (!d) return '';
    const date = new Date(d);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    if (diff < 86400000) return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    if (diff < 604800000) return date.toLocaleDateString('pt-BR', { weekday: 'short' });
    return date.toLocaleDateString('pt-BR');
  };

  if (!selectedSectorId) {
    return (
      <div className="email-page">
        <div className="email-empty-state">
          <FiMail size={48} />
          <p>Selecione uma conexão para ver os e-mails</p>
        </div>
      </div>
    );
  }

  if (connectionLoading) {
    return (
      <div className="email-page">
        <div className="email-loading">
          <LoadingSpinner size="lg" color="primary" />
        </div>
      </div>
    );
  }

  if (!hasConnection) {
    return (
      <div className="email-page">
        <div className="email-empty-state">
          <FiMail size={48} />
          <p>Nenhuma caixa de e-mail conectada</p>
          <p className="email-empty-hint">Conecte o Gmail, o Microsoft 365 ou uma conta SMTP/IMAP em Configurações › Conexões para trazer os e-mails. Depois disso eles aparecem aqui e também em Conversas, junto com o WhatsApp de cada cliente.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="email-page">
      <div className="email-sidebar">
        <button className="email-compose-btn" onClick={() => setShowCompose(true)}>
          <FiPlus size={18} />
          Escrever
        </button>
        <nav className="email-folders">
          {FOLDERS.map((f) => (
            <button
              key={f.id}
              className={`email-folder ${folder === f.id ? 'active' : ''}`}
              onClick={() => setFolder(f.id)}
            >
              <f.icon size={18} />
              <span>{f.label}</span>
            </button>
          ))}
        </nav>
        <button type="button" className="email-folder email-sync-now" onClick={() => void syncNow()} title="Ler a caixa agora (o sistema já lê sozinho a cada 2 minutos)">
          <FiInbox size={18} />
          <span>Atualizar agora</span>
        </button>
      </div>

      <div className="email-main">
        <div className="email-list-panel">
          {loading ? (
            <div className="email-list-loading">
              <LoadingSpinner size="md" color="primary" />
            </div>
          ) : emails.length === 0 ? (
            <div className="email-list-empty">
              <p>Nenhum e-mail em {FOLDERS.find((f) => f.id === folder)?.label.toLowerCase() ?? folder}</p>
            </div>
          ) : (
            <ul className="email-list">
              {emails.map((e) => (
                <li
                  key={e.id}
                  className={`email-list-item ${selectedEmail?.id === e.id ? 'selected' : ''} ${!e.isRead ? 'unread' : ''}`}
                  onClick={() => handleSelectEmail(e.id)}
                >
                  <div className="email-list-row">
                    <button
                      className="email-star-btn"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        handleToggleStar(e.id);
                      }}
                      title={e.isStarred ? 'Tirar estrela' : 'Marcar com estrela'}
                    >
                      <FiStar size={16} fill={e.isStarred ? 'currentColor' : 'none'} />
                    </button>
                    <span className="email-from">{e.from || '(sem remetente)'}</span>
                    <span className="email-snippet">{e.snippet || e.subject || '(sem assunto)'}</span>
                    <span className="email-date">{formatDate(e.dateReceived)}</span>
                  </div>
                  <div className="email-subject">
                    {e.subject || '(sem assunto)'}
                    {(() => {
                      const reason = linkReason(e.linkStatus);
                      return reason ? (
                        <span className={`email-link-reason email-link-reason--${e.linkStatus ?? 'pending'}`} title={reason.detail}>
                          {reason.label}
                        </span>
                      ) : null;
                    })()}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {total > 20 && (
            <div className="email-pagination">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Anterior
              </button>
              <span>
                Página {page} de {Math.ceil(total / 20)}
              </span>
              <button
                disabled={page >= Math.ceil(total / 20)}
                onClick={() => setPage((p) => p + 1)}
              >
                Próxima
              </button>
            </div>
          )}
        </div>

        <div className="email-detail-panel">
          {detailLoading ? (
            <div className="email-detail-loading">
              <LoadingSpinner size="md" color="primary" />
            </div>
          ) : selectedEmail ? (
            <div className="email-detail">
              <div className="email-detail-header">
                <h2>{selectedEmail.subject || '(sem assunto)'}</h2>
                <div className="email-detail-actions">
                  <button
                    className="email-action-btn"
                    onClick={() => handleToggleStar(selectedEmail.id)}
                    title={selectedEmail.isStarred ? 'Tirar estrela' : 'Marcar com estrela'}
                  >
                    <FiStar size={18} fill={selectedEmail.isStarred ? 'currentColor' : 'none'} />
                  </button>
                  {selectedEmail.folder === 'trash' ? (
                    <>
                      <button
                        className="email-action-btn"
                        onClick={() => handleMoveToInbox(selectedEmail.id)}
                        title="Devolver para a caixa de entrada"
                      >
                        <FiInbox size={18} />
                      </button>
                      <button
                        className="email-action-btn"
                        onClick={() => handleDeletePermanently(selectedEmail.id)}
                        title="Excluir definitivamente"
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </>
                  ) : selectedEmail.folder === 'spam' ? (
                    <button
                      className="email-action-btn"
                      onClick={() => handleMoveToInbox(selectedEmail.id)}
                      title="Devolver para a caixa de entrada"
                    >
                      <FiInbox size={18} />
                    </button>
                  ) : (
                    <>
                      <button
                        className="email-action-btn"
                        onClick={() => handleMoveToTrash(selectedEmail.id)}
                        title="Mover para a lixeira"
                      >
                        <FiTrash2 size={18} />
                      </button>
                      <button
                        className="email-action-btn"
                        onClick={() => handleMoveToSpam(selectedEmail.id)}
                        title="Marcar como spam"
                      >
                        <FiAlertTriangle size={18} />
                      </button>
                    </>
                  )}
                  {selectedEmail.folder !== 'trash' && selectedEmail.folder !== 'spam' && (
                    <button className="email-reply-btn" onClick={() => setShowReply(true)}>
                      Responder
                    </button>
                  )}
                </div>
              </div>
              <div className="email-detail-meta">
                <span>De: {selectedEmail.from}</span>
                <span>Para: {selectedEmail.to}</span>
                {selectedEmail.dateReceived && (
                  <span>{new Date(selectedEmail.dateReceived).toLocaleString('pt-BR')}</span>
                )}
              </div>
              <EmailLinkPanel
                email={selectedEmail}
                sectorId={selectedSectorId}
                onLinked={(contactId) => {
                  setSelectedEmail({ ...selectedEmail, contactId, linkStatus: 'linked_manual' });
                  fetchEmails(true);
                  addToast('E-mail ligado ao cliente. Ele já aparece em Conversas.', 'success');
                }}
                onError={(message) => addToast(message, 'error')}
              />
              <div className="email-detail-body">
                {selectedEmail.bodyHtml ? (
                  <div dangerouslySetInnerHTML={{ __html: selectedEmail.bodyHtml }} />
                ) : (
                  <pre>{selectedEmail.bodyText || '(sem conteúdo)'}</pre>
                )}
              </div>
              {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                <div className="email-attachments">
                  <strong>Anexos:</strong>
                  <ul>
                    {selectedEmail.attachments.map((a) => (
                      <li key={a.id}>
                        {a.downloadUrl ? (
                          <a href={a.downloadUrl} target="_blank" rel="noopener noreferrer" className="email-attachment-link">
                            {a.filename || 'anexo'}
                          </a>
                        ) : (
                          a.filename || 'anexo'
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="email-detail-empty">
              <p>Escolha um e-mail para ler</p>
            </div>
          )}
        </div>
      </div>

      {showCompose && selectedSectorId && (
        <ComposeModal
          sectorId={selectedSectorId}
          onClose={() => setShowCompose(false)}
          onSend={handleSend}
          sending={sending}
          addToast={addToast}
        />
      )}

      {showReply && selectedEmail && selectedSectorId && (
        <ReplyModal
          sectorId={selectedSectorId}
          onClose={() => setShowReply(false)}
          onSend={handleReply}
          sending={sending}
          originalEmail={selectedEmail}
          addToast={addToast}
        />
      )}

      {confirmAction && confirmEmailId && (
        <ConfirmModal
          open={true}
          onClose={closeConfirm}
          onConfirm={handleConfirmAction}
          loading={confirmLoading}
          variant="danger"
          title={
            confirmAction === 'moveTrash'
              ? 'Mover para a lixeira?'
              : confirmAction === 'moveSpam'
                ? 'Marcar como spam?'
                : 'Excluir definitivamente?'
          }
          message={
            confirmAction === 'moveTrash'
              ? 'O e-mail vai para a lixeira. Dá para devolver depois.'
              : confirmAction === 'moveSpam'
                ? 'O e-mail será marcado como spam.'
                : 'Não dá para desfazer: o e-mail será excluído de vez também na sua caixa (Gmail, Microsoft ou servidor).'
          }
          confirmLabel={confirmAction === 'deletePermanently' ? 'Excluir definitivamente' : 'Confirmar'}
        />
      )}

      <ToastContainer>
        {toasts.map((t) => (
          <Toast key={t.id} message={t.message} type={t.type} onClose={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))} />
        ))}
      </ToastContainer>
    </div>
  );
};

/**
 * Motivo do e-mail não estar em Conversas e, quando uma pessoa pode decidir (incerto, homônimo, sem cliente…), ligar a
 * um cliente existente do setor ou criar um cliente novo com o endereço do e-mail.
 */
const EmailLinkPanel: React.FC<{
  email: EmailDetail;
  sectorId: number | null | undefined;
  onLinked: (contactId: number) => void;
  onError: (message: string) => void;
}> = ({ email, sectorId, onLinked, onError }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<ContactListDTO[]>([]);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setSearch('');
    setResults([]);
    setOpen(false);
  }, [email.id]);

  useEffect(() => {
    if (!open || !sectorId || search.trim().length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const page = await getContactsPaged(sectorId, 1, 8, search);
        if (!cancelled) setResults(page.items);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, search, sectorId]);

  const reason = linkReason(email.linkStatus);
  if (!reason) {
    return email.contactId ? (
      <div className="email-link-panel email-link-panel--linked">Este e-mail está na conversa do cliente em Conversas.</div>
    ) : null;
  }
  const canLink = !!email.linkStatus && MANUALLY_LINKABLE_STATUSES.includes(email.linkStatus);

  const link = async (contactId?: number) => {
    setLinking(true);
    try {
      const res = await linkEmailToContact(email.id, contactId);
      onLinked(res.contactId);
    } catch (err: any) {
      const code = err?.response?.data?.code;
      onError(
        code === 'already_linked'
          ? 'Este e-mail já está ligado a um cliente.'
          : code === 'no_address'
            ? 'Este e-mail não tem um endereço válido para criar o cliente.'
            : 'Não foi possível ligar o e-mail ao cliente.'
      );
    } finally {
      setLinking(false);
    }
  };

  return (
    <div className="email-link-panel">
      <div className="email-link-panel-reason">
        <span className={`email-link-reason email-link-reason--${email.linkStatus ?? 'pending'}`}>{reason.label}</span>
        <span>{reason.detail}</span>
      </div>
      {canLink && !open && (
        <button type="button" className="email-link-panel-toggle" onClick={() => setOpen(true)}>
          Ligar a um cliente
        </button>
      )}
      {canLink && open && (
        <div className="email-link-panel-form">
          <input
            type="search"
            placeholder="Buscar cliente pelo nome ou telefone"
            value={search}
            onChange={(ev) => setSearch(ev.target.value)}
            disabled={linking}
            autoFocus
          />
          {searching && <span className="email-link-panel-hint">Buscando…</span>}
          {linking && <span className="email-link-panel-hint">Ligando ao cliente…</span>}
          {results.length > 0 && (
            <ul className="email-link-panel-results">
              {results.map((c) => (
                <li key={c.id}>
                  <button type="button" disabled={linking} onClick={() => link(c.id)}>
                    <strong>{c.name}</strong>
                    {c.phone ? <span>{c.phone}</span> : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {!searching && search.trim().length >= 2 && results.length === 0 && (
            <span className="email-link-panel-hint">Nenhum cliente encontrado.</span>
          )}
          <div className="email-link-panel-actions">
            {email.counterpartEmail && (
              <button type="button" disabled={linking} onClick={() => link()}>
                Criar cliente com {email.counterpartEmail}
              </button>
            )}
            <button type="button" className="email-link-panel-cancel" disabled={linking} onClick={() => setOpen(false)}>
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ComposeModal: React.FC<{
  sectorId: number;
  onClose: () => void;
  onSend: (req: SendEmailRequest) => void;
  sending: boolean;
  addToast: (message: string, type: 'success' | 'error') => void;
}> = ({ sectorId, onClose, onSend, sending, addToast }) => {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState<{ filename: string; contentType?: string; base64Content: string }[]>([]);
  const [aiOptimizing, setAiOptimizing] = useState<'subject' | 'body' | null>(null);

  const handleAiOptimize = async (type: 'subject' | 'body') => {
    setAiOptimizing(type);
    try {
      const plainBody = htmlToText(body);
      const text = type === 'subject' ? subject : plainBody;
      const context = type === 'subject' ? plainBody : subject;
      const result = await optimizeText(sectorId, type, text, undefined, context || undefined, 'campaign');
      if (type === 'subject') setSubject(result);
      else setBody(result);
      addToast('Texto melhorado pela IA', 'success');
    } catch (e) {
      addToast((e as Error)?.message || 'Não foi possível melhorar com a IA.', 'error');
    } finally {
      setAiOptimizing(null);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const base64 = await fileToBase64(file);
      setAttachments((prev) => [...prev, { filename: file.name, contentType: file.type || undefined, base64Content: base64 }]);
    }
    e.target.value = '';
  };

  const removeAttachment = (idx: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!to.trim()) return;
    if (isBodyEmpty(body)) {
      addToast('Escreva a mensagem', 'error');
      return;
    }
    onSend({
      to: to.trim(),
      cc: cc.trim() || undefined,
      bcc: bcc.trim() || undefined,
      subject,
      body,
      isHtml: true,
      attachments: attachments.length > 0 ? attachments : undefined,
    });
  };

  return (
    <div className="email-modal-overlay">
      <div className="email-modal">
        <div className="email-modal-header">
          <h3>Nova mensagem</h3>
          <button className="email-modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="email-form-row">
            <label>Para</label>
            <input type="email" value={to} onChange={(e) => setTo(e.target.value)} required placeholder="destinatario@exemplo.com" />
          </div>
          <div className="email-form-row">
            <label>Cc</label>
            <input type="text" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="copia@exemplo.com" />
          </div>
          <div className="email-form-row">
            <label>Cco</label>
            <input type="text" value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="copia-oculta@exemplo.com" />
          </div>
          <div className="email-form-row">
            <label>Assunto</label>
            <div className="field-with-optimize">
              <div className="field-with-optimize__header">
                <button type="button" className="field-with-optimize__btn" title="Melhorar com a IA" disabled={aiOptimizing !== null} onClick={() => handleAiOptimize('subject')}>
                  {aiOptimizing === 'subject' ? <LoadingSpinner size="sm" color="white" /> : 'Melhorar'}
                </button>
              </div>
              <input type="text" className="field-with-optimize__input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Assunto" />
            </div>
          </div>
          <div className="email-form-row">
            <label>Mensagem</label>
            <div className="field-with-optimize">
              <div className="field-with-optimize__header">
                <button type="button" className="field-with-optimize__btn" title="Melhorar com a IA" disabled={aiOptimizing !== null} onClick={() => handleAiOptimize('body')}>
                  {aiOptimizing === 'body' ? <LoadingSpinner size="sm" color="white" /> : 'Melhorar'}
                </button>
              </div>
              <EmailBodyEditor
                value={body}
                onChange={setBody}
                placeholder="Mensagem... (Ctrl+V cola imagens no texto)"
                rows={8}
                disabled={aiOptimizing !== null}
                className="field-with-optimize__textarea field-with-optimize__body-editor"
              />
            </div>
          </div>
          <div className="email-form-row email-form-row--attachments">
            <label>
              <FiPaperclip size={16} />
              Anexos
            </label>
            <div className="email-attachment-zone">
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                id="compose-attachments-input"
                className="email-attachment-input-hidden"
              />
              <label htmlFor="compose-attachments-input" className="email-attachment-trigger">
                <span className="email-attachment-trigger__icon"><FiPaperclip size={16} /></span>
                <span>{attachments.length > 0 ? 'Adicionar mais arquivos' : 'Adicionar arquivos'}</span>
              </label>
              {attachments.length > 0 && (
                <ul className="email-attachment-list">
                  {attachments.map((a, i) => (
                    <li key={i}>
                      {a.filename}
                      <button type="button" onClick={() => removeAttachment(i)} className="email-attachment-remove">×</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="email-modal-actions">
            <button type="button" onClick={onClose}>Cancelar</button>
            <button type="submit" disabled={sending}>
              {sending ? 'Enviando...' : 'Enviar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ReplyModal: React.FC<{
  sectorId: number;
  onClose: () => void;
  onSend: (req: ReplyEmailRequest) => void;
  sending: boolean;
  originalEmail: EmailDetail;
  addToast: (message: string, type: 'success' | 'error') => void;
}> = ({ sectorId, onClose, onSend, sending, originalEmail, addToast }) => {
  const defaultSubject = (originalEmail.subject ?? '').startsWith('Re:')
    ? (originalEmail.subject ?? '')
    : `Re: ${originalEmail.subject ?? ''}`;
  const [to, setTo] = useState(originalEmail.from || '');
  const [cc, setCc] = useState(originalEmail.cc || '');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState<{ filename: string; contentType?: string; base64Content: string }[]>([]);
  const [aiOptimizing, setAiOptimizing] = useState(false);

  const handleAiOptimize = async () => {
    setAiOptimizing(true);
    try {
      const plainBody = htmlToText(body);
      const result = await optimizeText(sectorId, 'body', plainBody, undefined, subject || undefined, 'campaign');
      setBody(result);
      addToast('Texto melhorado pela IA', 'success');
    } catch (e) {
      addToast((e as Error)?.message || 'Não foi possível melhorar com a IA.', 'error');
    } finally {
      setAiOptimizing(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const base64 = await fileToBase64(file);
      setAttachments((prev) => [...prev, { filename: file.name, contentType: file.type || undefined, base64Content: base64 }]);
    }
    e.target.value = '';
  };

  const removeAttachment = (idx: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isBodyEmpty(body)) {
      addToast('Escreva a mensagem', 'error');
      return;
    }
    onSend({
      to: to.trim() || undefined,
      cc: cc.trim() || undefined,
      bcc: bcc.trim() || undefined,
      subject: subject.trim() || undefined,
      body,
      isHtml: true,
      attachments: attachments.length > 0 ? attachments : undefined,
    });
  };

  return (
    <div className="email-modal-overlay">
      <div className="email-modal email-modal--reply">
        <div className="email-modal-header">
          <h3>Responder</h3>
          <button className="email-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="email-reply-original">
          <div className="email-reply-original__label">Mensagem original</div>
          <div className="email-reply-original__meta">
            De: {originalEmail.from} · {originalEmail.dateReceived && new Date(originalEmail.dateReceived).toLocaleString('pt-BR')}
          </div>
          <div className="email-reply-original__body">
            {originalEmail.bodyHtml ? (
              <div dangerouslySetInnerHTML={{ __html: originalEmail.bodyHtml }} />
            ) : (
              <pre>{originalEmail.bodyText || '(sem conteúdo)'}</pre>
            )}
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="email-form-row">
            <label>Para</label>
            <input type="text" value={to} onChange={(e) => setTo(e.target.value)} placeholder="destinatario@exemplo.com" />
          </div>
          <div className="email-form-row">
            <label>Cc</label>
            <input type="text" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="copia@exemplo.com" />
          </div>
          <div className="email-form-row">
            <label>Cco</label>
            <input type="text" value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="copia-oculta@exemplo.com" />
          </div>
          <div className="email-form-row">
            <label>Assunto</label>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Assunto" />
          </div>
          <div className="email-form-row">
            <label>Mensagem</label>
            <div className="field-with-optimize">
              <div className="field-with-optimize__header">
                <button type="button" className="field-with-optimize__btn" title="Melhorar com a IA" disabled={aiOptimizing} onClick={handleAiOptimize}>
                  {aiOptimizing ? <LoadingSpinner size="sm" color="white" /> : 'Melhorar'}
                </button>
              </div>
              <EmailBodyEditor
                value={body}
                onChange={setBody}
                placeholder="Sua resposta... (Ctrl+V cola imagens no texto)"
                rows={8}
                disabled={aiOptimizing}
                className="field-with-optimize__textarea field-with-optimize__body-editor"
              />
            </div>
          </div>
          <div className="email-form-row email-form-row--attachments">
            <label>
              <FiPaperclip size={16} />
              Anexos
            </label>
            <div className="email-attachment-zone">
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                id="reply-attachments-input"
                className="email-attachment-input-hidden"
              />
              <label htmlFor="reply-attachments-input" className="email-attachment-trigger">
                <span className="email-attachment-trigger__icon"><FiPaperclip size={16} /></span>
                <span>{attachments.length > 0 ? 'Adicionar mais arquivos' : 'Adicionar arquivos'}</span>
              </label>
              {attachments.length > 0 && (
                <ul className="email-attachment-list">
                  {attachments.map((a, i) => (
                    <li key={i}>
                      {a.filename}
                      <button type="button" onClick={() => removeAttachment(i)} className="email-attachment-remove">×</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="email-modal-actions">
            <button type="button" onClick={onClose}>Cancelar</button>
            <button type="submit" disabled={sending}>
              {sending ? 'Enviando...' : 'Enviar resposta'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailPage;
